import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";

import { clx } from "../../../utils/Helpers";
import { getUserInitials } from "../../../utils/userInitials";
import "./EntityCell.scss";

/**
 * Avatar + name, with an optional subtitle (e.g. "Active since Jan 2025", "2
 * events evaluated"). The avatar shows `imageUrl` when there is one (and it
 * loads), otherwise the initials. `variant` sets the initials colour — event =
 * primary, company = fuchsia, client = soft primary. `size` "md" enlarges the
 * avatar + name for card layouts. The name is truncated with a tooltip.
 * `autoDirection` lays a name that contains Arabic letters out right-to-left
 * (a name is written in one language, so it may not match the page's).
 */
const ARABIC_LETTER = /[؀-ۿ]/;

const EntityCell = ({
  variant,
  size,
  name,
  subtitle,
  imageUrl,
  autoDirection,
  className,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <div
      className={clx(
        "entity-cell",
        `entity-cell--${variant}`,
        `entity-cell--${size}`,
        className,
      )}
    >
      <span className="entity-cell__avatar" aria-hidden="true">
        {imageUrl && !imageFailed ? (
          <img
            className="entity-cell__image"
            src={imageUrl}
            alt=""
            onError={() => setImageFailed(true)}
          />
        ) : (
          getUserInitials(name, 2)
        )}
      </span>
      <span className="entity-cell__copy">
        <Tooltip title={name} placement="top" arrow>
          <span
            className={clx(
              "entity-cell__name",
              autoDirection &&
                ARABIC_LETTER.test(name) &&
                "entity-cell__name--rtl",
            )}
          >
            {name}
          </span>
        </Tooltip>
        {subtitle ? (
          <span className="entity-cell__subtitle">{subtitle}</span>
        ) : null}
      </span>
    </div>
  );
};

EntityCell.propTypes = {
  variant: PropTypes.oneOf(["event", "company", "client"]),
  size: PropTypes.oneOf(["sm", "md"]),
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  imageUrl: PropTypes.string,
  autoDirection: PropTypes.bool,
  className: PropTypes.string,
};

EntityCell.defaultProps = {
  variant: "event",
  size: "sm",
  subtitle: "",
  imageUrl: null,
  autoDirection: false,
  className: "",
};

export default EntityCell;
